import React, { useState } from 'react';
import { BannerSet, BannerSize } from '../types';
import { X, Globe, Copy, CheckCircle, Settings, Zap, Link, Upload, Image as ImageIcon, Code, AlertCircle, Download, FileText } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { deployBannerToProduction, batchDeployToProduction, checkProductionHealth } from '../utils/realDeploymentUtils';

interface BannerDeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  bannerSet: BannerSet;
  clientId: string;
  clientName: string;
  onUpdateBannerSet: (updatedBannerSet: Partial<BannerSet>) => void;
}

interface DeploymentConfig {
  vehicleType: string;
  priority: number;
  isActive: boolean;
  staticUrl: string;
  imageUrl: string;
  deploymentType: 'html' | 'image';
  imageFormat: 'png' | 'jpeg';
  refreshInterval: number;
  lastDeployed?: Date;
}

export const BannerDeploymentModal: React.FC<BannerDeploymentModalProps> = ({
  isOpen,
  onClose,
  bannerSet,
  clientId,
  clientName,
  onUpdateBannerSet
}) => {
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [deploymentConfigs, setDeploymentConfigs] = useState<Record<string, DeploymentConfig>>({});
  const [deploymentType, setDeploymentType] = useState<'html' | 'image'>('image');
  const [imageFormat, setImageFormat] = useState<'png' | 'jpeg'>('png');
  const [refreshInterval, setRefreshInterval] = useState(3);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployedUrls, setDeployedUrls] = useState<Record<string, { html: string; image?: string }>>({});
  const [productionHealth, setProductionHealth] = useState<any>(null);
  const [deploymentResults, setDeploymentResults] = useState<Array<{
    size: BannerSize;
    htmlFile: string;
    uploadPath: string;
    imageUrl: string;
  }>>([]);
  
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  // Get completed sizes only
  const completedSizes = bannerSet.sizes.filter(size => size.elements.length > 0);

  // Vehicle types for preset organization
  const vehicleTypes = [
    'accord', 'civic', 'crv', 'hrv', 'pilot', 'passport', 'ridgeline', 
    'odyssey', 'insight', 'clarity', 'general', 'service', 'parts'
  ];

  // Move generateStaticUrl function before useEffect
  const generateStaticUrl = (clientId: string, size: BannerSize, vehicleType: string, priority: number): string => {
    const sanitizedClientId = clientId.toLowerCase().replace(/[^a-z0-9]/g, '');
    const sanitizedSize = size.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `https://banners.dealercentive.us/${sanitizedClientId}/${vehicleType}/${sanitizedSize}-${priority}.html`;
  };

  const generateStaticImageUrl = (clientId: string, size: BannerSize, vehicleType: string, priority: number, format: string): string => {
    const sanitizedClientId = clientId.toLowerCase().replace(/[^a-z0-9]/g, '');
    const sanitizedSize = size.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `https://images.dealercentive.us/${sanitizedClientId}/${vehicleType}/${sanitizedSize}-${priority}.${format}`;
  };

  React.useEffect(() => {
    if (completedSizes.length > 0 && selectedSizes.length === 0) {
      setSelectedSizes(completedSizes.map(size => size.id));
      
      // Initialize deployment configs
      const configs: Record<string, DeploymentConfig> = {};
      completedSizes.forEach(size => {
        configs[size.id] = {
          vehicleType: 'general',
          priority: 1,
          isActive: true,
          staticUrl: generateStaticUrl(clientId, size, 'general', 1),
          imageUrl: generateStaticImageUrl(clientId, size, 'general', 1, imageFormat),
          deploymentType,
          imageFormat,
          refreshInterval
        };
      });
      setDeploymentConfigs(configs);
    }

    // Check production health
    checkProductionHealth().then(setProductionHealth);
  }, [completedSizes, selectedSizes.length, clientId, deploymentType, imageFormat, refreshInterval]);

  if (!isOpen) return null;

  const handleSizeToggle = (sizeId: string) => {
    setSelectedSizes(prev => 
      prev.includes(sizeId) 
        ? prev.filter(id => id !== sizeId)
        : [...prev, sizeId]
    );
  };

  const handleConfigChange = (sizeId: string, field: keyof DeploymentConfig, value: any) => {
    setDeploymentConfigs(prev => {
      const updated = {
        ...prev,
        [sizeId]: {
          ...prev[sizeId],
          [field]: value
        }
      };
      
      // Regenerate URLs if relevant fields change
      if (field === 'vehicleType' || field === 'priority' || field === 'imageFormat') {
        const size = completedSizes.find(s => s.id === sizeId);
        if (size) {
          const vehicleType = field === 'vehicleType' ? value : updated[sizeId].vehicleType;
          const priority = field === 'priority' ? value : updated[sizeId].priority;
          const format = field === 'imageFormat' ? value : updated[sizeId].imageFormat;
          
          updated[sizeId].staticUrl = generateStaticUrl(clientId, size, vehicleType, priority);
          updated[sizeId].imageUrl = generateStaticImageUrl(clientId, size, vehicleType, priority, format);
        }
      }
      
      return updated;
    });
  };

  const handleGlobalSettingsChange = (setting: string, value: any) => {
    if (setting === 'deploymentType') {
      setDeploymentType(value);
    } else if (setting === 'imageFormat') {
      setImageFormat(value);
    } else if (setting === 'refreshInterval') {
      setRefreshInterval(value);
    }

    // Update all configs
    setDeploymentConfigs(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(sizeId => {
        const size = completedSizes.find(s => s.id === sizeId);
        if (size) {
          updated[sizeId] = {
            ...updated[sizeId],
            deploymentType: setting === 'deploymentType' ? value : deploymentType,
            imageFormat: setting === 'imageFormat' ? value : imageFormat,
            refreshInterval: setting === 'refreshInterval' ? value : refreshInterval
          };
          
          // Regenerate image URL if format changed
          if (setting === 'imageFormat') {
            updated[sizeId].imageUrl = generateStaticImageUrl(
              clientId, 
              size, 
              updated[sizeId].vehicleType, 
              updated[sizeId].priority, 
              value
            );
          }
        }
      });
      return updated;
    });
  };

  const handleDeploy = async () => {
    if (selectedSizes.length === 0) {
      showError('Deployment Error', 'Please select at least one banner size to deploy.');
      return;
    }

    if (bannerSet.dataFeedArray.length === 0) {
      showError('Deployment Error', 'Please upload a data feed first to deploy dynamic banners.');
      return;
    }

    setIsDeploying(true);
    
    try {
      // Check production health first
      const healthStatuses = await checkProductionHealth();
      const isDown = healthStatuses.some(status => status.status === 'down');
      const isDegraded = healthStatuses.some(status => status.status === 'degraded');

      if (isDown) {
        showError('Deployment Error', 'Production services are currently unavailable. Please try again later.');
        setIsDeploying(false);
        return;
      }

      if (isDegraded) {
        showWarning('Service Warning', 'Some production services are experiencing issues. Deployment may be slower than usual.');
      }

      showInfo('Production Deployment Started', `Preparing ${selectedSizes.length} banner${selectedSizes.length !== 1 ? 's' : ''} for deployment...`);
      
      // Save deployment settings to banner set
      const deploymentSettings = {
        autoRefreshInterval: refreshInterval,
        deploymentType,
        imageFormat,
        lastDeployed: new Date(),
        enableAnalytics: false,
        deploymentConfigs: Object.fromEntries(
          selectedSizes
            .filter(sizeId => deploymentConfigs[sizeId]?.isActive)
            .map(sizeId => [
              sizeId,
              {
                vehicleType: deploymentConfigs[sizeId].vehicleType,
                priority: deploymentConfigs[sizeId].priority,
                isActive: deploymentConfigs[sizeId].isActive,
                staticUrl: deploymentConfigs[sizeId].staticUrl
              }
            ])
        )
      };

      // Update banner set with deployment settings
      onUpdateBannerSet({
        deploymentSettings,
        lastModified: new Date()
      });
      
      // Prepare deployments
      const deployments = selectedSizes
        .filter(sizeId => deploymentConfigs[sizeId]?.isActive)
        .map(sizeId => {
          const size = completedSizes.find(s => s.id === sizeId)!;
          const config = deploymentConfigs[sizeId];
          
          return {
            elements: size.elements,
            size,
            dataFeedArray: bannerSet.dataFeedArray,
            config: {
              vehicleType: config.vehicleType,
              priority: config.priority,
              isActive: config.isActive,
              deploymentType,
              imageFormat,
              refreshInterval
            },
            clientId
          };
        });

      // Deploy to production using real APIs
      const results = await batchDeployToProduction(deployments);
      
      // Process results and create deployment instructions
      const deploymentInstructions: Array<{
        size: BannerSize;
        htmlFile: string;
        uploadPath: string;
        imageUrl: string;
      }> = [];
      
      let successCount = 0;
      let errorCount = 0;

      results.forEach(result => {
        if (result.success) {
          const size = completedSizes.find(s => s.id === result.sizeId)!;
          const config = deploymentConfigs[result.sizeId];
          
          deploymentInstructions.push({
            size,
            htmlFile: `${clientId}_${config.vehicleType}_${size.name.replace(/[^a-z0-9]/gi, '')}-${config.priority}.html`,
            uploadPath: `${clientId}/${config.vehicleType}/${size.name.replace(/[^a-z0-9]/gi, '').toLowerCase()}-${config.priority}.html`,
            imageUrl: result.imageUrl || ''
          });
          
          successCount++;

          // Update deployment config with last deployed time
          setDeploymentConfigs(prev => ({
            ...prev,
            [result.sizeId]: {
              ...prev[result.sizeId],
              lastDeployed: new Date()
            }
          }));
        } else {
          errorCount++;
          showError('Deployment Failed', `Failed to deploy banner: ${result.error || 'Unknown error'}`);
        }
      });
      
      setDeploymentResults(deploymentInstructions);
      
      if (successCount > 0) {
        const deploymentTypeText = deploymentType === 'image' 
          ? `auto-refreshing ${imageFormat.toUpperCase()} images (${refreshInterval}min refresh)`
          : 'HTML banners';
        
        showSuccess('HTML Files Generated', 
          `Generated ${successCount} HTML file${successCount !== 1 ? 's' : ''} for manual upload. Please upload them to your R2 bucket to complete deployment.`);
        
        showInfo('Manual Upload Required', 
          'Due to browser security restrictions, HTML files must be manually uploaded to Cloudflare R2. See the upload instructions below.');
      }

      if (errorCount > 0) {
        showWarning('Partial Deployment', `${errorCount} banner${errorCount !== 1 ? 's' : ''} failed to deploy. Check the logs for details.`);
      }
    } catch (error) {
      console.error('Deployment failed:', error);
      showError('Deployment Error', error instanceof Error ? error.message : 'An unexpected error occurred during deployment');
    } finally {
      setIsDeploying(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied', 'Text copied to clipboard');
  };

  const generateEmbedCode = (url: string, size: BannerSize, isImage: boolean): string => {
    if (isImage) {
      return `<!-- DealerCentives Auto-Refreshing Image Banner -->
<img 
  src="${url}" 
  alt="DealerCentives Banner"
  width="${size.width}" 
  height="${size.height}"
  style="max-width: 100%; height: auto; display: block;"
  loading="lazy">`;
    } else {
      return `<!-- DealerCentives Dynamic Banner -->
<iframe 
  src="${url}" 
  width="${size.width}" 
  height="${size.height}" 
  frameborder="0" 
  scrolling="no"
  style="border: none; display: block;">
</iframe>`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Deploy to Production</h2>
              <p className="text-sm text-gray-600">Deploy banners to live URLs with auto-refresh capability</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isDeploying}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Production Health Status */}
          {productionHealth && (
            <div className={`p-4 rounded-lg border ${
              productionHealth.status === 'healthy' ? 'bg-green-50 border-green-200' :
              productionHealth.status === 'degraded' ? 'bg-yellow-50 border-yellow-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center space-x-2 mb-2">
                {productionHealth.status === 'healthy' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                )}
                <h3 className={`font-medium ${
                  productionHealth.status === 'healthy' ? 'text-green-900' :
                  productionHealth.status === 'degraded' ? 'text-yellow-900' :
                  'text-red-900'
                }`}>
                  Production Status: {productionHealth.status.charAt(0).toUpperCase() + productionHealth.status.slice(1)}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                {productionHealth.services.map((service: any, index: number) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${service.status === 'up' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-gray-700">{service.name}</span>
                    {service.responseTime && (
                      <span className="text-gray-500">({service.responseTime}ms)</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deployment Type Selection */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-3">Deployment Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                deploymentType === 'image' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="deploymentType"
                  value="image"
                  checked={deploymentType === 'image'}
                  onChange={(e) => handleGlobalSettingsChange('deploymentType', e.target.value)}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <ImageIcon size={16} className="text-blue-600" />
                    <span className="font-medium text-gray-900">Static Images (Recommended)</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Auto-refreshing PNG/JPEG images that update every {refreshInterval} minutes using HTMLCSStoImage.com API. 
                    Perfect for websites, social media, and email campaigns.
                  </p>
                  <div className="mt-2 text-xs text-blue-700">
                    ✓ Faster loading • ✓ Better compatibility • ✓ Auto-refresh • ✓ SEO friendly
                  </div>
                </div>
              </label>

              <label className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                deploymentType === 'html' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="deploymentType"
                  value="html"
                  checked={deploymentType === 'html'}
                  onChange={(e) => handleGlobalSettingsChange('deploymentType', e.target.value)}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Code size={16} className="text-blue-600" />
                    <span className="font-medium text-gray-900">Interactive HTML</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Full HTML banners with animations and interactivity. 
                    Best for advanced campaigns requiring user interaction.
                  </p>
                  <div className="mt-2 text-xs text-blue-700">
                    ✓ Animations • ✓ Click tracking • ✓ Interactive elements
                  </div>
                </div>
              </label>
            </div>

            {/* Image-specific settings */}
            {deploymentType === 'image' && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image Format</label>
                  <select
                    value={imageFormat}
                    onChange={(e) => handleGlobalSettingsChange('imageFormat', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="png">PNG (Best Quality)</option>
                    <option value="jpeg">JPEG (Smaller Size)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Interval</label>
                  <select
                    value={refreshInterval}
                    onChange={(e) => handleGlobalSettingsChange('refreshInterval', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={2}>2 minutes</option>
                    <option value={3}>3 minutes</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <div className="text-xs text-gray-600">
                    Images auto-update when you publish changes to banners using Cloudflare Workers
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Banner Configuration */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Banner Configuration</h3>
            
            <div className="space-y-4">
              {completedSizes.map((size) => (
                <div
                  key={size.id}
                  className={`border border-gray-200 rounded-lg p-4 ${
                    selectedSizes.includes(size.id) ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    {/* Selection Checkbox */}
                    <label className="flex items-center mt-1">
                      <input
                        type="checkbox"
                        checked={selectedSizes.includes(size.id)}
                        onChange={() => handleSizeToggle(size.id)}
                        disabled={isDeploying}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                    </label>

                    {/* Banner Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{size.name}</h4>
                          <p className="text-sm text-gray-600">{size.width} × {size.height} • {size.elements.length} elements</p>
                        </div>
                        
                        {deploymentConfigs[size.id]?.lastDeployed && (
                          <div className="text-xs text-green-600 flex items-center space-x-1">
                            <CheckCircle size={12} />
                            <span>Deployed {deploymentConfigs[size.id].lastDeployed?.toLocaleTimeString()}</span>
                          </div>
                        )}
                      </div>

                      {selectedSizes.includes(size.id) && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          {/* Vehicle Type */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Vehicle Type
                            </label>
                            <select
                              value={deploymentConfigs[size.id]?.vehicleType || 'general'}
                              onChange={(e) => handleConfigChange(size.id, 'vehicleType', e.target.value)}
                              disabled={isDeploying}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                            >
                              {vehicleTypes.map(type => (
                                <option key={type} value={type}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Priority */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Priority
                            </label>
                            <select
                              value={deploymentConfigs[size.id]?.priority || 1}
                              onChange={(e) => handleConfigChange(size.id, 'priority', parseInt(e.target.value))}
                              disabled={isDeploying}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                            >
                              {[1, 2, 3, 4, 5].map(priority => (
                                <option key={priority} value={priority}>
                                  Priority {priority}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Active Status */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={deploymentConfigs[size.id]?.isActive ?? true}
                                onChange={(e) => handleConfigChange(size.id, 'isActive', e.target.checked)}
                                disabled={isDeploying}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                              />
                              <span className="text-sm text-gray-700">Active</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* URL Display */}
                      {selectedSizes.includes(size.id) && deploymentConfigs[size.id] && (
                        <div className="space-y-3">
                          {/* Primary URL (Image or HTML) */}
                          <div className="bg-white border border-gray-200 rounded p-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-gray-700">
                                {deploymentType === 'image' ? 'Production Image URL:' : 'Production HTML URL:'}
                              </label>
                              <button
                                onClick={() => copyToClipboard(
                                  deploymentType === 'image' 
                                    ? deploymentConfigs[size.id].imageUrl 
                                    : deploymentConfigs[size.id].staticUrl
                                )}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                              >
                                <Copy size={12} />
                                <span>Copy</span>
                              </button>
                            </div>
                            <code className="text-xs text-gray-800 bg-gray-50 p-2 rounded block break-all">
                              {deploymentType === 'image' 
                                ? deploymentConfigs[size.id].imageUrl 
                                : deploymentConfigs[size.id].staticUrl}
                            </code>
                          </div>

                          {/* Additional info for image deployment */}
                          {deploymentType === 'image' && (
                            <div className="bg-green-50 border border-green-200 rounded p-3">
                              <div className="text-xs text-green-800 space-y-1">
                                <div><strong>Auto-refresh:</strong> Every {refreshInterval} minutes via Cloudflare Worker</div>
                                <div><strong>Format:</strong> {imageFormat.toUpperCase()}</div>
                                <div><strong>Quality:</strong> High (2x resolution via HTMLCSStoImage.com)</div>
                                <div><strong>Source HTML:</strong> {deploymentConfigs[size.id].staticUrl}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {completedSizes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Globe className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p>No completed banner sizes available for deployment.</p>
                <p className="text-sm mt-1">Please design at least one banner first.</p>
              </div>
            )}
          </div>

          {/* Deployment Summary */}
          {selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Production Deployment Summary</h4>
              <div className="text-sm text-green-800 space-y-1">
                <div>Selected banners: {selectedSizes.length} of {completedSizes.length}</div>
                <div>Data variations: {bannerSet.dataFeedArray.length}</div>
                <div>Client: {clientName}</div>
                <div>Type: {deploymentType === 'image' ? `Auto-refreshing ${imageFormat.toUpperCase()} images` : 'Interactive HTML'}</div>
                {deploymentType === 'image' && (
                  <div>Refresh interval: {refreshInterval} minutes</div>
                )}
                <div className="font-medium">Deploying to production Cloudflare infrastructure</div>
                <div className="text-xs text-green-600 mt-2">
                  URLs are permanent - only content updates when you publish changes.
                </div>
              </div>
            </div>
          )}

          {/* Data Feed Warning */}
          {bannerSet.dataFeedArray.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-1">No Data Feed</h4>
              <p className="text-sm text-yellow-700">
                Please upload a data feed first to enable dynamic banner deployment.
              </p>
            </div>
          )}

          {/* Manual Upload Instructions */}
          {deploymentResults.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-medium text-orange-900 mb-4 flex items-center space-x-2">
                <Upload className="w-5 h-5" />
                <span>Manual Upload Required</span>
              </h4>
              
              <div className="text-sm text-orange-800 space-y-4">
                <p>Due to browser security restrictions, HTML files must be manually uploaded to your Cloudflare R2 bucket:</p>
                
                <div className="space-y-3">
                  {deploymentResults.map((result, index) => (
                    <div key={index} className="bg-white border border-orange-200 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-orange-900">{result.size.name}</span>
                        <span className="text-xs text-orange-600">Downloaded: {result.htmlFile}</span>
                      </div>
                      
                      <div className="space-y-2 text-xs">
                        <div>
                          <strong>Upload to R2 path:</strong>
                          <code className="ml-2 bg-orange-100 px-1 rounded">{result.uploadPath}</code>
                          <button
                            onClick={() => copyToClipboard(result.uploadPath)}
                            className="ml-2 text-orange-600 hover:text-orange-800"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                        <div>
                          <strong>Auto-refresh image URL:</strong>
                          <code className="ml-2 bg-orange-100 px-1 rounded text-xs break-all">{result.imageUrl}</code>
                          <button
                            onClick={() => copyToClipboard(result.imageUrl)}
                            className="ml-2 text-orange-600 hover:text-orange-800"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-white border border-orange-200 rounded p-3">
                  <h5 className="font-medium text-orange-900 mb-2">Upload Steps:</h5>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Go to your Cloudflare Dashboard → R2 Object Storage</li>
                    <li>Open the <code>dealercentive-banners</code> bucket</li>
                    <li>Create the folder structure as shown above</li>
                    <li>Upload each HTML file to its corresponding path</li>
                    <li>Test the auto-refresh image URLs</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Implementation Guide */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Production Implementation Guide</h4>
            <div className="text-sm text-gray-700 space-y-2">
              <div><strong>1. Deploy Banners:</strong> Click "Deploy to Production" to generate HTML files</div>
              <div><strong>2. Manual Upload:</strong> Upload the downloaded HTML files to your R2 bucket</div>
              <div><strong>3. Copy URLs:</strong> Use the provided production URLs in your website code</div>
              <div><strong>4. Embed on Website:</strong> Add {deploymentType === 'image' ? 'image' : 'iframe'} code to your web pages</div>
              <div><strong>5. Update Content:</strong> When you modify banners and republish, URLs stay the same but content updates</div>
              {deploymentType === 'image' && (
                <div><strong>6. Auto-Refresh:</strong> Images automatically update every {refreshInterval} minutes with latest content via Cloudflare Workers</div>
              )}
              <div><strong>{deploymentType === 'image' ? '7' : '6'}. Organize by Vehicle:</strong> Use vehicle types and priorities to manage multiple banners</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {isDeploying ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                <span>Generating HTML files for manual upload...</span>
              </div>
            ) : selectedSizes.length > 0 && bannerSet.dataFeedArray.length > 0 ? (
              `Ready to generate ${selectedSizes.filter(id => deploymentConfigs[id]?.isActive).length} HTML file${selectedSizes.filter(id => deploymentConfigs[id]?.isActive).length !== 1 ? 's' : ''} for manual upload`
            ) : (
              'Select banners and ensure data feed is uploaded'
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={isDeploying}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Close
            </button>
            <button
              onClick={handleDeploy}
              disabled={selectedSizes.length === 0 || bannerSet.dataFeedArray.length === 0 || isDeploying || !selectedSizes.some(id => deploymentConfigs[id]?.isActive)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText size={16} />
              <span>{isDeploying ? 'Generating...' : 'Generate HTML Files'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};